import React, { useEffect, useMemo, useState } from 'react';
import { getMarketingConsent, setMarketingConsent } from '../../lib/consent';
import { initMetaPixel } from '../../lib/analytics';
import './MarketingConsentBanner.css';

const MarketingConsentBanner: React.FC = () => {
  const initialConsent = useMemo(() => getMarketingConsent(), []);
  const [pathname, setPathname] = useState(() => (typeof window === 'undefined' ? '' : window.location.pathname));
  const isAppSurface = pathname.startsWith('/app');
  const [visible, setVisible] = useState(initialConsent === 'unknown' && !isAppSurface);
  const [launchNoticeOpen, setLaunchNoticeOpen] = useState(false);
  const [productFeedbackOpen, setProductFeedbackOpen] = useState(false);

  useEffect(() => {
    const syncLaunchNotice = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail;
      setLaunchNoticeOpen(Boolean(detail?.open));
    };
    window.addEventListener('ai-marketing:launch-notice-change', syncLaunchNotice);
    return () => window.removeEventListener('ai-marketing:launch-notice-change', syncLaunchNotice);
  }, []);

  useEffect(() => {
    const syncProductFeedback = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail;
      setProductFeedbackOpen(Boolean(detail?.open));
    };
    window.addEventListener('ai-marketing:product-feedback-change', syncProductFeedback);
    return () => window.removeEventListener('ai-marketing:product-feedback-change', syncProductFeedback);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncPathname = () => setPathname(window.location.pathname);
    syncPathname();
    const interval = window.setInterval(syncPathname, 500);
    window.addEventListener('popstate', syncPathname);
    window.addEventListener('hashchange', syncPathname);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('popstate', syncPathname);
      window.removeEventListener('hashchange', syncPathname);
    };
  }, []);

  useEffect(() => {
    if (isAppSurface) {
      setVisible(false);
    } else if (getMarketingConsent() === 'unknown') {
      setVisible(true);
    }
  }, [isAppSurface]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousPadding = document.body.style.paddingBottom;
    if (visible) {
      document.body.style.paddingBottom = '128px';
    } else {
      document.body.style.paddingBottom = previousPadding;
    }
    return () => {
      document.body.style.paddingBottom = previousPadding;
    };
  }, [visible]);

  // The launch notice explains access before any optional analytics choice.
  // Rendering both fixed overlays at once obscures the only actionable buttons on mobile.
  if (!visible || launchNoticeOpen || productFeedbackOpen) return null;

  const accept = () => {
    setMarketingConsent('granted');
    initMetaPixel();
    setVisible(false);
  };

  const deny = () => {
    setMarketingConsent('denied');
    setVisible(false);
  };

  return (
    <aside
      className="marketing-consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label="마케팅 쿠키 동의"
    >
      <div className="marketing-consent-banner__content">
        <strong>분석 쿠키 설정</strong>
        <p>
          서비스 개선과 캠페인 성과 측정을 위해 선택형 쿠키를 사용합니다.
          <a href="/legal/privacy.html" target="_blank" rel="noreferrer">
            개인정보처리방침 보기
          </a>
        </p>
      </div>
      <div className="marketing-consent-banner__actions">
        <button
          type="button"
          className="consent-btn consent-btn--ghost"
          onClick={deny}
          aria-label="선택형 쿠키 거부"
        >
          거부
        </button>
        <button
          type="button"
          className="consent-btn consent-btn--primary"
          onClick={accept}
          aria-label="선택형 쿠키 허용"
        >
          허용
        </button>
      </div>
    </aside>
  );
};

export default MarketingConsentBanner;
